import React, { useState } from "react";
import {
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Paper,
  Typography,
} from "@mui/material";
import {
  Description,
  Cancel,
  Notes as NotesIcon,
  Close,
  Delete,
  Info,
  EventRepeat,
  ContentPaste,
  Done,
  Preview,
  Today,
  Edit,
  RecordVoiceOver,
  CallSplit,
  SwapHoriz,
} from "@mui/icons-material";
import { Event, TimeSlotExt } from "./Calendar.type";
import { useNavigate } from "react-router-dom";
import AppointmentNotes from "./AppointmentNotes";
import MoveScopeDialog from "./MoveScopeDialog";
import EditIcon from "@mui/icons-material/Edit";
import useStore from "../../helpers/useStore";
import { AppointmentContext } from "../../stores/Appointment/appointment.provider";
import { CalendarContext } from "../../stores/Calendar/calendar.provider";
import { AppointmentDto, ParticipantDto } from "../../api-client";
import {
  CalendarEvent,
  adaptAppointmentsToCalendarEvents,
  ClientTimeSlot,
} from "./controller/adapters";
import { adaptContinuousAppointmentsToCalendarEvents } from "./controller/adapters/batch.utilities";
import { PatientCommands } from "../../stores/Patients/patient.commands";
import { PatientsContext } from "../../stores/Patients/patients.provider";
import { PATIENT_SUBTABS } from "../../stores/Patients/patientsStore";
import {
  eventToClientTimeSlot,
  materializeCalendarEvent,
} from "./controller/adapters/display-to-client/reverse.adapter";
interface ContextMenuProps {
  event: CalendarEvent | undefined;
  timeSlot: ClientTimeSlot;
  open: boolean;
  continuous: boolean;
  mouseX: number | null;
  mouseY: number | null;
  onClose: () => void;
  onRemoveGoneFishing: (event: CalendarEvent) => void;
  onRemoveEventTimeSlot: (event: CalendarEvent) => void;
  onRemoveContinuousEventTimeSlot: (event: CalendarEvent) => void;
  onEditEventTitle: (event: CalendarEvent) => void;
  onRemoveGroupTherapist: (event: CalendarEvent) => void;
  onRemoveLeaveOfAbsence: (event: CalendarEvent) => void;
  onRemoveSimpleWorkingHours: (event: CalendarEvent) => void;
  /** Split a lunch break into two halves (MD-238). Only shown for non-continuous breaks. */
  onSplitLunchBreak?: (event: CalendarEvent) => void;
  onCancelDay?: (userId: string, date: string, userName: string) => void;
  onTimeSlotClick?: (clickInfo: ClientTimeSlot) => void;
  onAddShelfEvent: (event: CalendarEvent) => void;
  onAddWaitListEvent: (event: CalendarEvent) => void;
  onMoveEvent: (event: CalendarEvent) => void;
  lookupByAppointmentId?: (appointmentId: string) => AppointmentDto | undefined;
  onCancelAppointment: (timeSlot: ClientTimeSlot) => void;
  onUpdateCancelledAppointment: (timeSlot: ClientTimeSlot) => void;
  onCancelContinuousAppointment: (timeSlot: ClientTimeSlot) => void;
  onToggleAttended: (timeSlot: ClientTimeSlot) => void;
  onEditAppointment: (timeSlot: ClientTimeSlot) => void;
  onSetInformed: (timeSlot: ClientTimeSlot) => void;
  onCloseNotesDialog: () => void;
  onAppointmentOverview: (patientId: number, rxId?: string) => void;
  onOpenNotesDialog: (timeSlot: ClientTimeSlot) => void;
}

export interface MousePosition {
  mouseX: null | number;
  mouseY: null | number;
}

const ContextMenu: React.FC<ContextMenuProps> = ({
  event,
  timeSlot,
  open,
  continuous,
  mouseX,
  mouseY,
  onClose,
  onRemoveGoneFishing,
  onRemoveEventTimeSlot,
  onRemoveContinuousEventTimeSlot,
  onEditEventTitle,
  onRemoveGroupTherapist,
  onRemoveLeaveOfAbsence,
  onRemoveSimpleWorkingHours,
  onSplitLunchBreak,
  onCancelDay,
  onTimeSlotClick,
  onMoveEvent,
  lookupByAppointmentId,
  onAddShelfEvent,
  onAddWaitListEvent,
  onCancelAppointment,
  onEditAppointment,
  onCancelContinuousAppointment,
  onUpdateCancelledAppointment,
  onToggleAttended,
  onSetInformed,
  onCloseNotesDialog,
  onAppointmentOverview,
  onOpenNotesDialog,
}) => {
  // console.log("ContextMenu render", timeSlot,event);
  // Central debug log for every render while attempting to diagnose invisible menu
  if (open) {
    // Avoid massive logs by summarizing
    // console.debug("[ContextMenu] render", {
      // type: event?.type,
      // open,
      // mouseX,
      // mouseY,
      // hasTimeSlot: !!timeSlot,
    // });
  }
  const navigate = useNavigate();
  const patientsStore = useStore(PatientsContext);
  const CalendarStore = useStore(CalendarContext);
  const { navigateToPatient } = PatientCommands(patientsStore);
  // const [showNotesDialog, setShowNotesDialog] = useState<boolean>(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [selectedGroupParticipant, setSelectedGroupParticipant] = useState<ParticipantDto | null>(
    null,
  );

  const [selectedParticipantTimeSlot, setSelectedParticipantTimeSlot] =
    useState<ClientTimeSlot | null>(timeSlot);

  const createParticipantCalendarEvent = (
    participant: ParticipantDto,
  ): { calendarEvent: CalendarEvent; timeSlot: ClientTimeSlot } | undefined => {
    const calendarEvent = continuous
      ? adaptContinuousAppointmentsToCalendarEvents([participant as any], {})[0]
      : adaptAppointmentsToCalendarEvents([participant as any], {})[0];
    if (!calendarEvent) return undefined;

    return { calendarEvent: calendarEvent, timeSlot: eventToClientTimeSlot(calendarEvent) };
  };

  const handleOpenMoveEvent = (event: CalendarEvent) => {
    setSelectedEvent(event);

    // A group participant's move-event is rebuilt from a participant DTO that
    // carries ONLY its own group slot (its appointmentId still points at the
    // patient's host appointment). So `multipleTimeSlots` is always false here
    // even when the host also holds a separate (e.g. MT) slot — which silently
    // skips this prompt and drags the WHOLE host appointment to the shelf.
    // Resolve the real host appointment to recover the split choice.
    const tsType = event.extendedProps?.timeSlotType;
    const isGroupParticipant =
      tsType === "GroupTimeSlotPatient" || tsType === "ContinuousGroupTimeSlotPatient";

    let hasMultipleTimeSlots = !!event.extendedProps?.multipleTimeSlots;
    if (!hasMultipleTimeSlots && isGroupParticipant && lookupByAppointmentId) {
      const hostAppointmentId = event.extendedProps?.appointmentId;
      const hostAppointment = hostAppointmentId
        ? lookupByAppointmentId(hostAppointmentId)
        : undefined;
      hasMultipleTimeSlots = (hostAppointment?.timeSlots?.length ?? 0) > 1;
    }

    if (hasMultipleTimeSlots) {
      setMoveDialogOpen(true);
    } else {
      onMoveEvent(event);
    }
  };

  const handleMoveAll = () => {
    setMoveDialogOpen(false);
    if (selectedEvent) {
      onMoveEvent(selectedEvent);
      setSelectedEvent(null);
    }
  };

  // "Nur diese": move a SINGLE slot of a multi-slot appointment to the shelf.
  // Pure client-side — no backend call happens here. The slot stays scheduled in
  // the DB until it is dropped somewhere (the slot-level reschedule verb splits
  // it off its appointment atomically at drop time), so closing the shelf
  // (X / Escape) fully cancels the move without any persisted side effects.
  const handleMoveSingle = () => {
    if (!selectedEvent?.id) {
      console.error("Error in handleMoveSingle: selected event or its id is missing");
      return;
    }
    setMoveDialogOpen(false);
    // selectedEvent is usually a live EventApi (getter-backed id/groupId), so
    // materialize before spreading — a plain spread drops the slot id and the
    // single-slot pin silently never happens.
    const plainEvent = materializeCalendarEvent(selectedEvent);
    onMoveEvent({
      ...plainEvent,
      extendedProps: { ...plainEvent.extendedProps, moveSingleSlot: true },
    });
    setSelectedEvent(null);
  };

  const handleTimeSlotKeyDown = (e: any) => {
    switch (e.key) {
      case "Escape":
        onClose();
        break;
      case "p":
        onPatientClick();
        break;
      case "v":
        // Same path as clicking "Umziehen" — multi-slot appointments get the
        // Alle/Nur-diese scope prompt instead of silently moving everything.
        handleOpenMoveEvent(event!);
        break;
      case "n":
        onNotesClick();
        break;
      case "r":
        onRxClick();
        break;
      case "x":
        // console.log("Cancel");
        onCancelAppointment(timeSlot);
        break;
      case "w":
        // console.log("Attended");
        onToggleAttended(timeSlot);
        break;
      case "b":
        // console.log("Edit");
        onEditAppointment(timeSlot);
        break;
      case "i":
        if (!continuous && timeSlot && !timeSlot.isCancelled) {
          onSetInformed(timeSlot);
          onClose();
        }
        break;

      default:
        break;
    }
  };

  const handleGoneFishingKeyDown = (e: any) => {
    switch (e.key) {
      case "Escape":
        onClose();
        break;
      case "d":
        onClose();
        onRemoveGoneFishing(event!);
        break;
      default:
        break;
    }
  };

  const handleEventTimeSlotKeyDown = (e: any) => {
    switch (e.key) {
      case "Escape":
        onClose();
        break;
      case "d":
        onClose();
        onRemoveEventTimeSlot(event!);
        break;
      default:
        break;
    }
  };

  const handleContinuousEventTimeSlotKeyDown = (e: any) => {
    switch (e.key) {
      case "Escape":
        onClose();
        break;
      case "d":
        onClose();
        onRemoveContinuousEventTimeSlot(event!);
        break;
      default:
        break;
    }
  };

  const handleLeaveOfAbsenceKeyDown = (e: any) => {
    switch (e.key) {
      case "Escape":
        onClose();
        break;
      case "d":
        onClose();
        onRemoveLeaveOfAbsence(event!);
        break;
      default:
        break;
    }
  };

  const handleSimpleWorkingHoursKeyDown = (e: any) => {
    switch (e.key) {
      case "Escape":
        onClose();
        break;
      case "d":
        onClose();
        onRemoveSimpleWorkingHours(event!);
        break;
      default:
        break;
    }
  };

  const handleDefualtKeyDown = (e: any) => {
    switch (e.key) {
      case "Escape":
        onClose();
        break;
      case "i":
        onAddShelfEvent(event!);
        break;
      default:
        break;
    }
  };

  const onPatientClick = () => {
    navigateToPatient(navigate, timeSlot.patient?.id, null, PATIENT_SUBTABS.APPOINTMENTS);
  };

  const onRxClick = () => {
    navigateToPatient(
      navigate,
      timeSlot.patient?.id,
      timeSlot?.rx?.id as any,
      PATIENT_SUBTABS.PRESCRIPTIONS,
    );
  };

  const onNotesClick = () => {
    onOpenNotesDialog(timeSlot);
    onClose();
  };

  const handleShowOverviewAppointmentModal = () => {
    onClose();
    return onAppointmentOverview(timeSlot?.patient?.id!, timeSlot?.rx?.id!);
  };

  const onCloseNotesDialogEvent = () => {
    // setShowNotesDialog(false);
    return onCloseNotesDialog();
  };

  // Normalize new adapter event types to legacy menu categories
  const rawType = event?.type;
  // Some events may now carry their classification inside extendedProps.type (new adapter);
  // prefer extendedProps.type if present.
  const adaptedType = event?.extendedProps?.timeSlotType || rawType;
  let normalizedType = adaptedType;

  // Map new or variant types to legacy ones expected by switch
  if (
    normalizedType === "Treatment" ||
    normalizedType === "SalesTimeSlot" ||
    normalizedType === "GroupTimeSlotPatient" ||
    normalizedType === "ContinuousGroupTimeSlotPatient"
  ) {
    // New adapter's appointment timeslot -> legacy 'TimeSlot'
    normalizedType = "TimeSlot";
  }

  // Map WorkingHours to SimpleWorkingHours for context menu
  if (normalizedType === "WorkingHours") {
    normalizedType = "SimpleWorkingHours";
  }

  // console.debug("[ContextMenu] Rendered with normalizedType:", { normalizedType, event });
  // Centralized menu list For KGG Participants
  const renderTimeSlotParticipantMenuList = (
    event: CalendarEvent | undefined,
    slot: ClientTimeSlot | null,
  ) => {
    if (!slot) return null;

    return (
      <MenuList>
        <MenuItem
          onClick={() =>
            navigateToPatient(navigate, slot.patient?.id!, null, PATIENT_SUBTABS.APPOINTMENTS)
          }
        >
          <ListItemIcon>
            <Info fontSize="small" />
          </ListItemIcon>
          <ListItemText>Patient</ListItemText>
          <Typography variant="body2" color="text.secondary">
            P
          </Typography>
        </MenuItem>

        <MenuItem onClick={() => onAppointmentOverview(slot.patient?.id!, slot?.rx?.id!)}>
          <ListItemIcon>
            <Preview fontSize="small" />
          </ListItemIcon>
          <ListItemText>Terminübersicht</ListItemText>
          <Typography variant="body2" color="text.secondary">
            Ü
          </Typography>
        </MenuItem>

        {event && (
          <MenuItem onClick={() => handleOpenMoveEvent(event)}>
            <ListItemIcon>
              <EventRepeat fontSize="small" />
            </ListItemIcon>
            <ListItemText>Verschieben</ListItemText>
            <Typography variant="body2" color="text.secondary">
              V
            </Typography>
          </MenuItem>
        )}
        <MoveScopeDialog
          open={moveDialogOpen}
          onCancel={() => setMoveDialogOpen(false)}
          onAll={handleMoveAll}
          onSingle={handleMoveSingle}
        />

        {!continuous && (
          <MenuItem
            onClick={() => {
              onOpenNotesDialog(slot);
              onClose();
            }}
          >
            <ListItemIcon>
              <NotesIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Terminhinweise</ListItemText>
            <Typography variant="body2" color="text.secondary">
              N
            </Typography>
          </MenuItem>
        )}

        <MenuItem
          onClick={() =>
            navigateToPatient(
              navigate,
              slot.patient?.id!,
              slot?.rx?.id as any,
              PATIENT_SUBTABS.PRESCRIPTIONS,
            )
          }
        >
          <ListItemIcon>
            <Description fontSize="small" />
          </ListItemIcon>
          <ListItemText>Rezept</ListItemText>
          <Typography variant="body2" color="text.secondary">
            R
          </Typography>
        </MenuItem>

        {slot.isCancelled ? (
          <MenuItem onClick={() => onUpdateCancelledAppointment(slot)}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>Abbruchgrund bearbeiten</ListItemText>
          </MenuItem>
        ) : (
          <>
            <MenuItem
              onClick={() =>
                continuous ? onCancelContinuousAppointment(slot) : onCancelAppointment(slot)
              }
            >
              <ListItemIcon>
                <Cancel fontSize="small" />
              </ListItemIcon>
              <ListItemText>Stornieren</ListItemText>
              <Typography variant="body2" color="text.secondary">
                X
              </Typography>
            </MenuItem>
            <MenuItem onClick={() => onEditAppointment(slot)}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Bearbeiten</ListItemText>
              <Typography variant="body2" color="text.secondary">
                B
              </Typography>
            </MenuItem>
            {/* MD-302: pick a non-default therapy method for this slot. The
                dialog itself explains when the term has no alternatives. */}
            {!continuous && slot?.id && (
              <MenuItem
                onClick={() => {
                  CalendarStore.openCalendarDialog({
                    kind: "therapyMethod",
                    timeSlotId: String(slot.id),
                  });
                  onClose();
                }}
              >
                <ListItemIcon>
                  <SwapHoriz fontSize="small" />
                </ListItemIcon>
                <ListItemText>Alternative Methoden</ListItemText>
              </MenuItem>
            )}
            {!continuous && (
              <MenuItem onClick={() => onToggleAttended(slot)}>
                <ListItemIcon>
                  <Done fontSize="small" />
                </ListItemIcon>
                <ListItemText sx={{ mr: 3 }}>Anwesenheit ändern</ListItemText>
                <Typography variant="body2" color="text.secondary">
                  W
                </Typography>
              </MenuItem>
            )}
          </>
        )}

        {!continuous && slot && !slot.isCancelled && (
          <MenuItem
            onClick={() => {
              onSetInformed(slot);
              onClose();
            }}
          >
            <ListItemIcon>
              <RecordVoiceOver fontSize="small" />
            </ListItemIcon>
            <ListItemText>Patient informieren</ListItemText>
            <Typography variant="body2" color="text.secondary">
              I
            </Typography>
          </MenuItem>
        )}

        <Divider />
        <MenuItem onClick={onClose}>
          <ListItemIcon>
            <Close fontSize="small" />
          </ListItemIcon>
          <ListItemText>Schließen</ListItemText>
          <Typography variant="body2" color="text.secondary">
            Esc
          </Typography>
        </MenuItem>
      </MenuList>
    );
  };

  switch (normalizedType) {
    case "TimeSlot":
      return (
        <>
          <Menu
            keepMounted
            open={open}
            onClose={onClose}
            onKeyDown={handleTimeSlotKeyDown}
            anchorReference="anchorPosition"
            anchorPosition={
              mouseY !== null && mouseX !== null ? { top: mouseY, left: mouseX } : undefined
            }
          >
            <MenuList>
              <MenuItem onClick={onPatientClick}>
                <ListItemIcon>
                  <Info fontSize="small" />
                </ListItemIcon>
                <ListItemText>Patient</ListItemText>
                <Typography variant="body2" color="text.secondary">
                  P
                </Typography>
              </MenuItem>
              <MenuItem onClick={handleShowOverviewAppointmentModal}>
                <ListItemIcon>
                  <Preview fontSize="small" />
                </ListItemIcon>
                {/* LANGUAGE */}
                <ListItemText>Terminübersicht</ListItemText>
                <Typography variant="body2" color="text.secondary">
                  Ü
                </Typography>
              </MenuItem>
              <MenuItem onClick={() => event && handleOpenMoveEvent(event)}>
                <ListItemIcon>
                  <EventRepeat fontSize="small" />
                </ListItemIcon>
                <ListItemText>Verschieben</ListItemText>
                <Typography variant="body2" color="text.secondary">
                  V
                </Typography>
              </MenuItem>
              <MoveScopeDialog
                open={moveDialogOpen}
                onCancel={() => setMoveDialogOpen(false)}
                onAll={handleMoveAll}
                onSingle={handleMoveSingle}
              />
              {!continuous && (
                <MenuItem onClick={onNotesClick}>
                  <ListItemIcon>
                    <NotesIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Terminhinweise</ListItemText>
                  <Typography variant="body2" color="text.secondary">
                    N
                  </Typography>
                </MenuItem>
              )}
              <MenuItem onClick={onRxClick}>
                <ListItemIcon>
                  <Description fontSize="small" />
                </ListItemIcon>
                <ListItemText>Rezept</ListItemText>
                <Typography variant="body2" color="text.secondary">
                  R
                </Typography>
              </MenuItem>
              {timeSlot && timeSlot.isCancelled && (
                <MenuItem onClick={() => onUpdateCancelledAppointment(timeSlot)}>
                  <ListItemIcon>
                    <Edit fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Abbruchgrund bearbeiten</ListItemText>
                </MenuItem>
              )}
              {timeSlot && !timeSlot.isCancelled && (
                <MenuItem
                  onClick={() =>
                    continuous
                      ? onCancelContinuousAppointment(timeSlot)
                      : onCancelAppointment(timeSlot)
                  }
                >
                  <ListItemIcon>
                    <Cancel fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Stornieren</ListItemText>
                  <Typography variant="body2" color="text.secondary">
                    X
                  </Typography>
                </MenuItem>
              )}
              {timeSlot && !timeSlot.isCancelled && (
                <MenuItem onClick={() => onEditAppointment(timeSlot)}>
                  <ListItemIcon>
                    <EditIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Bearbeiten</ListItemText>
                  <Typography variant="body2" color="text.secondary">
                    B
                  </Typography>
                </MenuItem>
              )}
              {/* MD-302: pick a non-default therapy method for this slot. The
                  dialog itself explains when the term has no alternatives. */}
              {!continuous && timeSlot && !timeSlot.isCancelled && timeSlot.id && (
                <MenuItem
                  onClick={() => {
                    CalendarStore.openCalendarDialog({
                      kind: "therapyMethod",
                      timeSlotId: String(timeSlot.id),
                    });
                    onClose();
                  }}
                >
                  <ListItemIcon>
                    <SwapHoriz fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Alternative Methoden</ListItemText>
                </MenuItem>
              )}
              {/* {!continuous && timeSlot && !timeSlot.isCancelled && (
                <MenuItem onClick={() => onToggleAttended(timeSlot)}>
                  <ListItemIcon>
                    <Done fontSize="small" />
                  </ListItemIcon>
                  <ListItemText sx={{ mr: 3 }}>Wahrgenommen</ListItemText>
                  <Typography variant="body2" color="text.secondary">
                    W
                  </Typography>
                </MenuItem>
              )} */}
              {!continuous && timeSlot && !timeSlot.isCancelled && (
                <MenuItem onClick={() => onToggleAttended(timeSlot)}>
                  <ListItemIcon>
                    <Done fontSize="small" />
                  </ListItemIcon>
                  <ListItemText sx={{ mr: 3 }}>Anwesenheit ändern</ListItemText>
                  <Typography variant="body2" color="text.secondary">
                    W
                  </Typography>
                </MenuItem>
              )}
              {!continuous && timeSlot && !timeSlot.isCancelled && (
                <MenuItem
                  onClick={() => {
                    onSetInformed(timeSlot);
                    onClose();
                  }}
                >
                  <ListItemIcon>
                    <RecordVoiceOver fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Patient informieren</ListItemText>
                  <Typography variant="body2" color="text.secondary">
                    I
                  </Typography>
                </MenuItem>
              )}
              <Divider />
              <MenuItem onClick={onClose}>
                <ListItemIcon>
                  <Close fontSize="small" />
                </ListItemIcon>
                <ListItemText>Schließen</ListItemText>
                <Typography variant="body2" color="text.secondary">
                  Esc
                </Typography>
              </MenuItem>
            </MenuList>
          </Menu>
        </>
      );
    case "GoneFishing":
      return (
        <Menu
          keepMounted
          open={open}
          onClose={onClose}
          onKeyDown={handleGoneFishingKeyDown}
          anchorReference="anchorPosition"
          anchorPosition={
            mouseY !== null && mouseX !== null ? { top: mouseY, left: mouseX } : undefined
          }
        >
          <MenuList>
            <MenuItem
              onClick={() => {
                onClose();
                event && onRemoveGoneFishing(event);
              }}
            >
              <ListItemIcon>
                <Delete fontSize="small" />
              </ListItemIcon>
              <ListItemText>Löschen</ListItemText>
              <Typography variant="body2" color="text.secondary">
                ⌘D
              </Typography>
            </MenuItem>
            <MenuItem onClick={onClose}>
              <ListItemIcon>
                <Close fontSize="small" />
              </ListItemIcon>
              <ListItemText>Schließen</ListItemText>
              <Typography variant="body2" color="text.secondary">
                Esc
              </Typography>
            </MenuItem>
          </MenuList>
        </Menu>
      );
    case "EventTimeSlot":
      return (
        <Menu
          keepMounted
          open={open}
          onClose={onClose}
          onKeyDown={handleEventTimeSlotKeyDown}
          anchorReference="anchorPosition"
          anchorPosition={
            mouseY !== null && mouseX !== null ? { top: mouseY, left: mouseX } : undefined
          }
        >
          <MenuList>
            <MenuItem
              onClick={() => {
                onClose();
                event && onEditEventTitle(event);
              }}
            >
              <ListItemIcon>
                <Edit fontSize="small" />
              </ListItemIcon>
              <ListItemText>Titel bearbeiten</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                onClose();
                event && onRemoveEventTimeSlot(event);
              }}
            >
              <ListItemIcon>
                <Delete fontSize="small" />
              </ListItemIcon>
              <ListItemText>Löschen</ListItemText>
              <Typography variant="body2" color="text.secondary">
                ⌘D
              </Typography>
            </MenuItem>
            <MenuItem onClick={onClose}>
              <ListItemIcon>
                <Close fontSize="small" />
              </ListItemIcon>
              <ListItemText>Schließen</ListItemText>
              <Typography variant="body2" color="text.secondary">
                Esc
              </Typography>
            </MenuItem>
          </MenuList>
        </Menu>
      );
    case "LunchBreak":
      return (
        <Menu
          keepMounted
          open={open}
          onClose={onClose}
          onKeyDown={handleEventTimeSlotKeyDown}
          anchorReference="anchorPosition"
          anchorPosition={
            mouseY !== null && mouseX !== null ? { top: mouseY, left: mouseX } : undefined
          }
        >
          <MenuList>
            {onSplitLunchBreak && !event?.extendedProps?.isContinuous && (
              <MenuItem
                onClick={() => {
                  onClose();
                  event && onSplitLunchBreak(event);
                }}
              >
                <ListItemIcon>
                  <CallSplit fontSize="small" />
                </ListItemIcon>
                <ListItemText>Pause teilen</ListItemText>
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                onClose();
                event && onRemoveEventTimeSlot(event);
              }}
            >
              <ListItemIcon>
                <Delete fontSize="small" />
              </ListItemIcon>
              <ListItemText>Löschen</ListItemText>
              <Typography variant="body2" color="text.secondary">
                ⌘D
              </Typography>
            </MenuItem>
            <MenuItem onClick={onClose}>
              <ListItemIcon>
                <Close fontSize="small" />
              </ListItemIcon>
              <ListItemText>Schließen</ListItemText>
              <Typography variant="body2" color="text.secondary">
                Esc
              </Typography>
            </MenuItem>
          </MenuList>
        </Menu>
      );
    case "TravelTime":
      return (
        <Menu
          keepMounted
          open={open}
          onClose={onClose}
          onKeyDown={handleEventTimeSlotKeyDown}
          anchorReference="anchorPosition"
          anchorPosition={
            mouseY !== null && mouseX !== null ? { top: mouseY, left: mouseX } : undefined
          }
        >
          <MenuList>
            <MenuItem
              onClick={() => {
                onClose();
                event && onRemoveEventTimeSlot(event);
              }}
            >
              <ListItemIcon>
                <Delete fontSize="small" />
              </ListItemIcon>
              <ListItemText>Löschen</ListItemText>
              <Typography variant="body2" color="text.secondary">
                ⌘D
              </Typography>
            </MenuItem>
            <MenuItem onClick={onClose}>
              <ListItemIcon>
                <Close fontSize="small" />
              </ListItemIcon>
              <ListItemText>Schließen</ListItemText>
              <Typography variant="body2" color="text.secondary">
                Esc
              </Typography>
            </MenuItem>
          </MenuList>
        </Menu>
      );
    case "LeaveOfAbsence":
      return (
        <Menu
          keepMounted
          open={open}
          onClose={onClose}
          onKeyDown={handleLeaveOfAbsenceKeyDown}
          anchorReference="anchorPosition"
          anchorPosition={
            mouseY !== null && mouseX !== null ? { top: mouseY, left: mouseX } : undefined
          }
        >
          <MenuList>
            <MenuItem
              onClick={() => {
                onClose();
                event && onRemoveLeaveOfAbsence(event);
              }}
            >
              <ListItemIcon>
                <Delete fontSize="small" />
              </ListItemIcon>
              <ListItemText>Löschen</ListItemText>
              <Typography variant="body2" color="text.secondary">
                ⌘D
              </Typography>
            </MenuItem>
            <MenuItem onClick={onClose}>
              <ListItemIcon>
                <Close fontSize="small" />
              </ListItemIcon>
              <ListItemText>Schließen</ListItemText>
              <Typography variant="body2" color="text.secondary">
                Esc
              </Typography>
            </MenuItem>
          </MenuList>
        </Menu>
      );
    case "SimpleWorkingHours":
      return (
        <Menu
          keepMounted
          open={open}
          onClose={onClose}
          onKeyDown={handleSimpleWorkingHoursKeyDown}
          anchorReference="anchorPosition"
          anchorPosition={
            mouseY !== null && mouseX !== null ? { top: mouseY, left: mouseX } : undefined
          }
        >
          <MenuList>
            <MenuItem
              onClick={() => {
                // console.log("[ContextMenu] Cancel Day clicked", { event, onCancelDay });
                onClose();
                if (onCancelDay) {
                  // Get resourceId from event - could be in different places depending on event type
                  const resourceId = event?.resourceId || event?.extendedProps?.user?.id;
                  // console.log("[ContextMenu] ResourceId check", {
                    // resourceId,
                    // eventResourceId: event?.resourceId,
                    // userInProps: event?.extendedProps?.user,
                  // });

                  if (resourceId) {
                    const userId = resourceId;
                    const userName =
                      event.extendedProps?.user?.displayName ||
                      event.extendedProps?.user?.fullName ||
                      event.extendedProps?.user?.id ||
                      "Unknown";
                    const date = event.startStr?.split("T")[0] || "";
                    // console.log("[ContextMenu] Calling onCancelDay", { userId, date, userName });
                    onCancelDay(userId, date, userName);
                  } else {
                    console.error("[ContextMenu] No resourceId found", event);
                  }
                }
              }}
            >
              <ListItemIcon>
                <Cancel fontSize="small" />
              </ListItemIcon>
              <ListItemText>Ganzen Tag absagen</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                onClose();
                event && onRemoveSimpleWorkingHours(event);
              }}
            >
              <ListItemIcon>
                <Delete fontSize="small" />
              </ListItemIcon>
              <ListItemText>Plan zurücksetzen</ListItemText>
              <Typography variant="body2" color="text.secondary">
                ⌘D
              </Typography>
            </MenuItem>
            <MenuItem onClick={onClose}>
              <ListItemIcon>
                <Close fontSize="small" />
              </ListItemIcon>
              <ListItemText>Schließen</ListItemText>
              <Typography variant="body2" color="text.secondary">
                Esc
              </Typography>
            </MenuItem>
          </MenuList>
        </Menu>
      );
    case "ShelfEvent":
      return (
        <Menu
          keepMounted
          open={open}
          onClose={onClose}
          onKeyDown={handleDefualtKeyDown}
          anchorReference="anchorPosition"
          anchorPosition={
            mouseY !== null && mouseX !== null ? { top: mouseY, left: mouseX } : undefined
          }
        >
          <MenuList>
            <MenuItem onClick={() => onAddShelfEvent(event!)}>
              <ListItemIcon>
                <ContentPaste fontSize="small" />
              </ListItemIcon>
              <ListItemText>Einfügen</ListItemText>
              <Typography variant="body2" color="text.secondary">
                ⌘I
              </Typography>
            </MenuItem>
            <MenuItem onClick={onClose}>
              <ListItemIcon>
                <Close fontSize="small" />
              </ListItemIcon>
              <ListItemText>Schließen</ListItemText>
              <Typography variant="body2" color="text.secondary">
                Esc
              </Typography>
            </MenuItem>
          </MenuList>
        </Menu>
      );
    case "WaitList":
      // Show waitlist menu only when not in continuous mode
      if (continuous) {
        return null;
      }
      return (
        <Menu
          keepMounted
          open={open}
          onClose={onClose}
          anchorReference="anchorPosition"
          anchorPosition={
            mouseY !== null && mouseX !== null ? { top: mouseY, left: mouseX } : undefined
          }
        >
          <MenuList>
            <MenuItem onClick={() => onAddWaitListEvent(event!)}>
              <ListItemIcon>
                <Today fontSize="small" />
              </ListItemIcon>
              <ListItemText>Lücken Füllen</ListItemText>
            </MenuItem>
          </MenuList>
        </Menu>
      );
    case "ContinuousEventTimeSlot":
      return (
        <Menu
          keepMounted
          open={open}
          onClose={onClose}
          onKeyDown={handleContinuousEventTimeSlotKeyDown}
          anchorReference="anchorPosition"
          anchorPosition={
            mouseY !== null && mouseX !== null ? { top: mouseY, left: mouseX } : undefined
          }
        >
          <MenuList>
            <MenuItem
              onClick={() => {
                onClose();
                event && onEditEventTitle(event);
              }}
            >
              <ListItemIcon>
                <Edit fontSize="small" />
              </ListItemIcon>
              <ListItemText>Titel bearbeiten</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                onClose();
                event && onRemoveContinuousEventTimeSlot(event);
              }}
            >
              <ListItemIcon>
                <Delete fontSize="small" />
              </ListItemIcon>
              <ListItemText>Löschen</ListItemText>
              <Typography variant="body2" color="text.secondary">
                ⌘D
              </Typography>
            </MenuItem>
            <MenuItem onClick={onClose}>
              <ListItemIcon>
                <Close fontSize="small" />
              </ListItemIcon>
              <ListItemText>Schließen</ListItemText>
              <Typography variant="body2" color="text.secondary">
                Esc
              </Typography>
            </MenuItem>
          </MenuList>
        </Menu>
      );
    case "GroupTimeSlotTherapist":
    case "ContinuousGroupTimeSlotTherapist":
      const calendarEvent = selectedGroupParticipant
        ? createParticipantCalendarEvent(selectedGroupParticipant)
        : undefined;

      return (
        <>
          <Menu
            keepMounted
            open={open}
            onClose={onClose}
            anchorReference="anchorPosition"
            anchorPosition={
              mouseY !== null && mouseX !== null ? { top: mouseY, left: mouseX } : undefined
            }
          >
            <MenuList sx={{ display: "flex", width: "100%", gap: 2 }}>
              {/* Left panel: Patient info */}
              {selectedGroupParticipant && selectedParticipantTimeSlot && (
                <Paper sx={{ px: 2, py: 1, minWidth: 200 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Patienten Info
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Name:</strong> {selectedParticipantTimeSlot.patient?.lastName},{" "}
                    {selectedParticipantTimeSlot.patient?.firstName}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Therapie:</strong>{" "}
                    {selectedParticipantTimeSlot.therapy?.abbreviation || "-"}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Rezept:</strong> {selectedParticipantTimeSlot?.rx?.rxNumber || "-"}
                  </Typography>

                  {renderTimeSlotParticipantMenuList(
                    calendarEvent?.calendarEvent!,
                    selectedParticipantTimeSlot,
                  )}
                </Paper>
              )}

              {/* Right panel: Participants list */}
              <div>
                <Typography variant="subtitle2" sx={{ px: 2, py: 1, fontWeight: 600 }}>
                  Gruppentermin - Teilnehmer
                </Typography>
                <Divider />
                {timeSlot?.participants?.length ? (
                  timeSlot.participants.map((participant: ParticipantDto) => {
                    const patient = participant.timeSlots?.[0]?.therapyRx?.rx?.patient;
                    const patientName = patient
                      ? `${patient.lastName}, ${patient.firstName}`
                      : "Patient";
                    const isSelected = selectedGroupParticipant?.id === participant.id;

                    return (
                      <MenuItem
                        key={participant.id}
                        onClick={() => {
                          setSelectedGroupParticipant(participant);
                          // update timeSlot for this participant
                          const newCalendar = createParticipantCalendarEvent(participant);
                          if (newCalendar) {
                            setSelectedParticipantTimeSlot(newCalendar.timeSlot);
                          }
                        }}
                        sx={{
                          bgcolor: isSelected ? "#e3f2fd" : "transparent",
                          fontWeight: isSelected ? 600 : 400,
                        }}
                      >
                        <ListItemText>{patientName}</ListItemText>
                      </MenuItem>
                    );
                  })
                ) : (
                  <><MenuItem disabled>
                      <ListItemText>Keine Teilnehmer</ListItemText>
                    </MenuItem><MenuItem
                      onClick={() => {
                        onClose();
                        event && onRemoveGroupTherapist(event);
                      } }
                    >
                        <ListItemIcon>
                          <Delete fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Löschen</ListItemText>
                        <Typography variant="body2" color="text.secondary">
                          
                        </Typography>
                      </MenuItem></>
                )}

                <Divider />
                <MenuItem onClick={onClose}>
                  <ListItemIcon>
                    <Close fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Schließen</ListItemText>
                  <Typography variant="body2" color="text.secondary">
                    Esc
                  </Typography>
                </MenuItem>
              </div>
            </MenuList>
          </Menu>
        </>
      );

    case "CancelledDay":
      return (
        <Menu
          keepMounted
          open={open}
          onClose={onClose}
          anchorReference="anchorPosition"
          anchorPosition={
            mouseY !== null && mouseX !== null ? { top: mouseY, left: mouseX } : undefined
          }
        >
          <MenuList>
            <MenuItem
              onClick={() => {
                onClose();
                event && onRemoveSimpleWorkingHours(event);
              }}
            >
              <ListItemIcon>
                <EventRepeat fontSize="small" />
              </ListItemIcon>
              <ListItemText>Plan zurücksetzen</ListItemText>
            </MenuItem>
            <MenuItem onClick={onClose}>
              <ListItemIcon>
                <Close fontSize="small" />
              </ListItemIcon>
              <ListItemText>Schließen</ListItemText>
              <Typography variant="body2" color="text.secondary">
                Esc
              </Typography>
            </MenuItem>
          </MenuList>
        </Menu>
      );
    default:
      // Fallback debug overlay so we SEE something even if type mismatches
      if (open && mouseX !== null && mouseY !== null) {
        return (
          <div
            style={{
              position: "fixed",
              top: mouseY,
              left: mouseX,
              background: "#fff",
              border: "2px solid #e53935",
              padding: "8px",
              zIndex: 99999,
              fontFamily: "monospace",
              maxWidth: 260,
              boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
            }}
          >
            <strong>DEBUG CONTEXT MENU</strong>
            <div style={{ fontSize: 11, marginTop: 4 }}>
              <div>Unknown type: {String(event?.type)}</div>
              <div>
                mouse: {mouseX},{mouseY}
              </div>
              <div>hasTimeSlot: {String(!!timeSlot)}</div>
            </div>
            <button style={{ marginTop: 6 }} onClick={onClose}>
              Schließen
            </button>
          </div>
        );
      }
      return null;
  }
};

export default ContextMenu;
